import ReactGA from 'react-ga4'
import {
  IUpdateGoogleAnalyticsProduct,
  IAnalyticsAddProduct,
  IAnalyticsAddProductWithAdditionalSeats,
  IAnalyticsRemoveProductWithAdditionalSeats,
  IGetElementsToRemove,
  IAnalyticsUpdateProduct,
  IAnalyticsItem
} from './analytics.types'
import { checkInternal } from '../../index'
import { getPriceBySku } from '../stripe'
import { hasAdditionalSeat, isAdditionalSeat, isBizzaboProduct } from '../../utils/bizzabo'
import { ICartItem, IPrice } from '../../interface'

export const analyticsUpdateProduct = ({
  isNewCartItemToBeAdded,
  selectedProduct,
  selectedItem,
  mainSeatQuantity,
  additionalSeatsQuantity,
  additionalSeatsQuantityDiff,
  newBizzaboSessionId,
  cart
}: IAnalyticsUpdateProduct) => {
  if (isNewCartItemToBeAdded) {
    analyticsAddProductAndAdditionalSeats({
      selectedProduct,
      selectedItem,
      mainSeatQuantity,
      additionalSeatsQuantity
    })
  } else {
    if (isBizzaboProduct(selectedProduct)) {
      // CartItem existed. We need to update the additional seats
      analyticsUpdateAdditionalSeatsProduct({
        selectedProduct,
        selectedItem,
        quantityDiff: additionalSeatsQuantityDiff,
        bizzaboSessionId: newBizzaboSessionId,
        cart
      })
    } else {
      // Only +1 can be added to the quantity for non-bizzabo products
      analyticsAddProduct({ selectedProduct, selectedItem, quantity: 1 })
    }
  }
}

const analyticsUpdateAdditionalSeatsProduct = ({
  selectedProduct,
  selectedItem,
  quantityDiff,
  bizzaboSessionId,
  cart
}: IUpdateGoogleAnalyticsProduct) => {
  const additionalSeat = hasAdditionalSeat(selectedProduct.items[selectedItem])
  if (quantityDiff > 0) {
    analyticsAddProduct({ selectedProduct, selectedItem, quantity: quantityDiff, isAdditionalSeat: additionalSeat })
  } else if (quantityDiff < 0) {
    const elementToDelete = getElementToRemove({ additionalSeat, bizzaboSessionId, cart })
    if (elementToDelete) {
      analyticsRemoveProduct(elementToDelete, Math.abs(quantityDiff))
    }
  }
}

/**
 * Get the element to remove from the cart. It can be:
 * - The main product if it's a bizzabo product and it's not an additional seat
 * - The additional seat if it's a bizzabo product and it's an additional seat
 */
const getElementToRemove = ({ additionalSeat, bizzaboSessionId, cart }: IGetElementsToRemove) => {
  return cart.find(
    (cartItem) =>
      cartItem.bizzaboSessionId === bizzaboSessionId &&
      (additionalSeat ? isAdditionalSeat(cartItem) : !isAdditionalSeat(cartItem))
  )
}

const analyticsAddProductAndAdditionalSeats = async ({
  selectedProduct,
  selectedItem,
  mainSeatQuantity,
  additionalSeatsQuantity
}: IAnalyticsAddProductWithAdditionalSeats) => {
  let quantity = 1
  if (isBizzaboProduct(selectedProduct)) {
    quantity = hasAdditionalSeat(selectedProduct.items[selectedItem]) ? mainSeatQuantity : additionalSeatsQuantity
  }
  analyticsAddProduct({ selectedProduct, selectedItem, quantity })
  if (hasAdditionalSeat(selectedProduct.items[selectedItem])) {
    analyticsAddProduct({ selectedProduct, selectedItem, quantity: additionalSeatsQuantity, isAdditionalSeat: true })
  }
}

const analyticsAddProduct = async ({
  selectedProduct,
  selectedItem,
  quantity = 1,
  isAdditionalSeat = false
}: IAnalyticsAddProduct) => {
  if (checkInternal() || quantity === 0) return
  let subProduct
  try {
    if (isAdditionalSeat) {
      // Additional seat is not in redux store because of its visibility, so we need to fetch it from the API
      subProduct = await getPriceBySku(selectedProduct.items[selectedItem].metadata._additionalSeat!)
    } else subProduct = selectedProduct.items[selectedItem]
    const discount = getDiscount(subProduct)
    const roundedDiscount = discount ? Math.round(discount * 100) / 100 : undefined
    const price = roundedDiscount ? getPrice(subProduct) - roundedDiscount : getPrice(subProduct)
    const roundedPrice = Math.round(price * 100) / 100
    if (subProduct) {
      const item: IAnalyticsItem = {
        item_id: subProduct.metadata._sku,
        item_name: selectedProduct.name,
        item_variant:
          selectedProduct.name === subProduct.metadata._version
            ? selectedProduct.name
            : `${selectedProduct.name} - ${subProduct.metadata._version}`,
        currency: 'USD',
        price: roundedPrice,
        quantity,
        discount: roundedDiscount
      }
      const categories = selectedProduct.category.split(',')
      categories.forEach((data, index) => {
        if (index <= 5) {
          if (index === 0) item['item_category'] = data
          else item[`item_category${index}`] = data
        }
      })
      ReactGA.gtag('event', 'add_to_cart', {
        currency: 'USD',
        items: [item],
        value: roundedPrice * quantity
      })
    }
  } catch (error) {
    console.error('Error on analyticsAddProduct - ', error)
  }
}

export const analyticsRemoveProductAndAdditionalSeats = ({
  cart,
  element
}: IAnalyticsRemoveProductWithAdditionalSeats) => {
  // Delete the main product
  analyticsRemoveProduct(element)

  // If it's a main product, find and delete additional seats related to him if any
  if (!isAdditionalSeat(element)) {
    const additionalSeatsElement = cart.find(
      (cartItem) => cartItem.bizzaboSessionId === element.bizzaboSessionId && isAdditionalSeat(cartItem)
    )
    if (additionalSeatsElement) analyticsRemoveProduct(additionalSeatsElement)
  }
}

const analyticsRemoveProduct = (product: ICartItem, quantity?: number) => {
  if (checkInternal()) return

  const description = product.description?.split('(')[0]
  const discount =
    product.unit_total !== product.unit_discount ? (product.unit_total - product.unit_discount) / 100 : undefined
  const roundedDiscount = discount ? Math.round(discount * 100) / 100 : undefined
  const priceWithoutDiscount = product.unit_total / 100
  const price = roundedDiscount ? priceWithoutDiscount - roundedDiscount : priceWithoutDiscount
  const roundedPrice = Math.round(price * 100) / 100
  const item: IAnalyticsItem = {
    item_id: product.productId,
    item_name: product.name,
    item_variant: description?.trim(),
    price: roundedPrice,
    currency: 'USD',
    quantity: quantity ?? product.quantity,
    discount: roundedDiscount
  }
  const categories = product.category?.split(',')
  categories?.forEach((data, index) => {
    if (index <= 5) {
      if (index === 0) item['item_category'] = data
      else item[`item_category${index}`] = data
    }
  })
  ReactGA.gtag('event', 'remove_from_cart', {
    currency: 'USD',
    items: [item],
    value: roundedPrice * (quantity ?? product.quantity)
  })
}

const getPrice = (product: IPrice) => {
  if (product.initialFeePrice) {
    return product.initialFeePrice / 100
  }
  return product.unit_amount / 100
}

const getDiscount = (product: IPrice) => {
  if (product.initialFeePricediscount) {
    return (product.initialFeePrice - product.initialFeePricediscount) / 100
  }
  if (product.discount) {
    return (product.unit_amount - product.discount) / 100
  }
  return undefined
}
